import { useState, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import jwt_decode from 'jwt-decode';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Grid,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  InputLabel,
  FormControl,
  CircularProgress,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { sendAndSaveEmail, uploadDocuments, getSignedUrl } from '../../api';
import { GenerateSOAPdf } from './GenerateSOAPdf';
import Swal from 'sweetalert2';
import ReactDOM from 'react-dom';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  cc: Yup.array().of(Yup.string().email('Invalid CC email address')),
  ccTemp: Yup.string().email('Invalid email address').notRequired(),
  bcc: Yup.array().of(Yup.string().email('Invalid BCC email address')),
  bccTemp: Yup.string().email('Invalid email address').notRequired(),
  attachments: Yup.array().of(Yup.string().url('Invalid attachment URL')),
  notes: Yup.string().required('Notes are required'),
  subject: Yup.string().required('Subject is required'),
});

const getEncodedUrl = (url) => {
  const match = url.match(/^https:\/\/rentdigicare\.s3\.tor1\.amazonaws\.com\/(.+)$/);
  if (!match) {
    return url;
  }
  let key = match[1];
  while (key.includes('%')) {
    const decoded = decodeURIComponent(key);
    if (decoded === key) break;
    key = decoded;
  }
  const encodedKey = key.split('/').map(segment => encodeURIComponent(segment)).join('/');
  return `https://rentdigicare.s3.tor1.amazonaws.com/${encodedKey}`;
};

const extractS3KeyAndType = (url) => {
  if (!url) return { key: '', fileType: '' };
  const urlPattern = /^https:\/\/rentdigicare\.s3\.tor1\.amazonaws\.com\/(.+)$/;
  const match = url.match(urlPattern);
  const encodedKey = match ? match[1] : url;
  const key = decodeURIComponent(encodedKey);
  const fileType = key.split('.').pop()?.toLowerCase() || '';
  return { key, fileType };
};

const SendSOAToTenant = ({ soaId, soaData }) => {
  const [expanded, setExpanded] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generatingSOAPdf, setGeneratingSOAPdf] = useState(false);
  const [downloadingSOAPdf, setDownloadingSOAPdf] = useState(false);
  const [soaPdfUrl, setSOAPdfUrl] = useState(null);
  const [ccError, setCcError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const decode = jwt_decode(window.localStorage.accessToken);
  const [managerEmail] = useState(decode.email);

  const formik = useFormik({
    initialValues: {
      name: soaData?.email_details?.name || soaData?.tenant?.[0]?.name || '',
      email: soaData?.email_details?.email || soaData?.tenant?.[0]?.email || '',
      cc: soaData?.email_details?.cc ? [...new Set([...soaData.email_details.cc, managerEmail])] : [managerEmail],
      ccTemp: '',
      bcc: soaData?.email_details?.bcc || [],
      bccTemp: '',
      attachments: soaData?.email_details?.attachments?.map(getEncodedUrl) || [],
      notes: soaData?.email_details?.notes || "Please find attached a quick summary of your rental account as of today.This is just for your review — let me know if you have any questions or need any clarification..",
      subject:
        soaData?.email_details?.subject ||
        `Quick Update on Your Rental Account – ${soaData?.address?.street && soaData?.address?.city && soaData?.address?.province
          ? `${soaData.address.street}, ${soaData.address.city}, ${soaData.address.province}`
          : soaData?.property_details?.unit_no || 'Property'
        }`,
    },
    validationSchema,
    enableReinitialize: true,
  });

  const handleSendEmail = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched({
        name: true,
        email: true,
        cc: true,
        bcc: true,
        notes: true,
        subject: true,
      });
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill out all required fields correctly.',
        icon: 'error',
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to send the email to the tenant?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, send it',
      cancelButtonText: 'No, cancel',
    });

    if (result.isConfirmed) {
      const loadingSwal = Swal.fire({
        title: 'Processing...',
        text: 'Sending email...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      try {
        setIsSubmitting(true);
        setGeneratingSOAPdf(true);
        let finalAttachments = [...formik.values.attachments];

        if (!soaPdfUrl) {
          let pdfUrl = await generateSOAPdfForEmail();
          pdfUrl = getEncodedUrl(pdfUrl);
          if (pdfUrl) {
            finalAttachments = [...finalAttachments, pdfUrl];
            setSOAPdfUrl(pdfUrl);
          } else {
            throw new Error('Failed to generate SOA PDF URL');
          }
        } else if (!finalAttachments.includes(soaPdfUrl)) {
          finalAttachments = [...finalAttachments, soaPdfUrl];
        }

        await sendAndSaveEmail(soaId, {
          ...formik.values,
          cc: [...new Set([...formik.values.cc, managerEmail])],
          bcc: formik.values.bcc,
          attachments: finalAttachments.map(url => {
            const match = url.match(/^https:\/\/rentdigicare\.s3\.tor1\.amazonaws\.com\/(.+)$/);
            return match ? `https://rentdigicare.s3.tor1.amazonaws.com/${encodeURIComponent(match[1])}` : url;
          }),
        });

        Swal.fire({
          title: 'Email Sent Successfully',
          text: 'Email sent to the tenant.',
          icon: 'success',
          timer: 3000,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: error.message || 'Failed to send email. Please try again.',
          icon: 'error',
          timer: 3000,
          showConfirmButton: false,
        });
      } finally {
        setIsSubmitting(false);
        setGeneratingSOAPdf(false);
        loadingSwal.close();

        const updatedAttachments = formik.values.attachments.filter(url => !isSOAPdf(url));
        formik.resetForm({
          values: {
            ...formik.initialValues,
            cc: [managerEmail],
            attachments: updatedAttachments,
          },
        });
        setSOAPdfUrl(null);
        setFiles([]);
        setCcError('');
      }
    }
  };

  const handleDownloadSOA = async () => {
    if (downloadingSOAPdf) return;

    setDownloadingSOAPdf(true);

    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    document.body.appendChild(tempDiv);

    const cleanup = () => {
      ReactDOM.unmountComponentAtNode(tempDiv);
      document.body.removeChild(tempDiv);
    };

    const handlePdfError = (error) => {
      cleanup();
      setDownloadingSOAPdf(false);
      Swal.fire({
        title: 'Error',
        text: error || 'Failed to generate SOA PDF for download.',
        icon: 'error',
        timer: 3000,
        showConfirmButton: false,
      });
    };

    const propertyId =
      soaData?.property_details?.property_id?._id ||
      soaData?.property_details?.property_id;
    if (!propertyId) {
      handlePdfError("Property ID is missing.");
      return;
    }

    try {
      ReactDOM.render(
        <GenerateSOAPdf
          soaId={soaId}
          downloadOnly={true}
          autoGenerate={true}
          onError={handlePdfError}
        />,
        tempDiv
      );

      await new Promise(resolve => setTimeout(resolve, 5000));

      cleanup();
      setDownloadingSOAPdf(false);
      Swal.fire({
        title: 'Download Started',
        text: 'SOA PDF is being downloaded.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      cleanup();
      setDownloadingSOAPdf(false);
      handlePdfError(error.message || "Error rendering GenerateSOAPdf");
    }
  };

  const allApprovalsDone =
    soaData?.approvals?.length > 0 &&
    soaData.approvals.every((a) => a.status === 'approved') &&
    soaData.auditor_approval?.status === 'approved';

  if (!allApprovalsDone) {
    return null;
  }

  const generateSOAPdfForEmail = () => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("PDF generation timed out"));
      }, 30000);

      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      document.body.appendChild(tempDiv);

      const cleanup = () => {
        clearTimeout(timeout);
        ReactDOM.unmountComponentAtNode(tempDiv);
        document.body.removeChild(tempDiv);
      };

      const handlePdfGenerated = (pdfUrl) => {
        cleanup();
        resolve(pdfUrl);
      };

      const handlePdfError = (error) => {
        cleanup();
        reject(new Error(error || "Failed to generate SOA PDF."));
      };

      const propertyId =
        soaData?.property_details?.property_id?._id ||
        soaData?.property_details?.property_id;
      if (!propertyId) {
        handlePdfError("Property ID is missing.");
        return;
      }

      try {
        ReactDOM.render(
          <GenerateSOAPdf
            soaId={soaId}
            autoGenerate={true}
            onPdfGenerated={handlePdfGenerated}
            onError={handlePdfError}
          />,
          tempDiv
        );
      } catch (error) {
        handlePdfError(error.message || "Error rendering GenerateSOAPdf");
      }
    });
  };

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  const handleAddCc = () => {
    if (formik.values.ccTemp && !formik.errors.ccTemp) {
      const emailToAdd = formik.values.ccTemp.trim().toLowerCase();
      const existingEmails = formik.values.cc.map(email => email.toLowerCase());

      if (existingEmails.includes(emailToAdd)) {
        setCcError('This email is already in the CC list.');
        return;
      }

      if (formik.values.email.toLowerCase() === emailToAdd) {
        setCcError('Cannot add recipient email to CC list.');
        return;
      }

      setCcError('');
      formik.setFieldValue('cc', [...formik.values.cc, formik.values.ccTemp]);
      formik.setFieldValue('ccTemp', '');
      formik.setFieldTouched('ccTemp', false);
    }
  };

  const handleDeleteCc = (emailToDelete) => {
    if (emailToDelete === managerEmail) {
      Swal.fire({
        title: 'Cannot Remove Manager',
        text: 'The manager email cannot be removed from CC.',
        icon: 'warning',
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }
    formik.setFieldValue('cc', formik.values.cc.filter((email) => email !== emailToDelete));
  };

  const handleAddBcc = () => {
    if (formik.values.bccTemp && !formik.errors.bccTemp) {
      const emailToAdd = formik.values.bccTemp.trim().toLowerCase();
      const existingEmails = formik.values.bcc.map(email => email.toLowerCase());

      if (existingEmails.includes(emailToAdd)) {
        Swal.fire({
          title: 'Duplicate Email',
          text: 'This email is already in the BCC list.',
          icon: 'warning',
          timer: 2000,
          showConfirmButton: false,
        });
        return;
      }

      if (formik.values.email.toLowerCase() === emailToAdd) {
        Swal.fire({
          title: 'Invalid Email',
          text: 'Cannot add recipient email to BCC list.',
          icon: 'warning',
          timer: 2000,
          showConfirmButton: false,
        });
        return;
      }

      formik.setFieldValue('bcc', [...formik.values.bcc, formik.values.bccTemp]);
      formik.setFieldValue('bccTemp', '');
      formik.setFieldTouched('bccTemp', false);
    }
  };

  const handleDeleteBcc = (emailToDelete) => {
    formik.setFieldValue('bcc', formik.values.bcc.filter((email) => email !== emailToDelete));
  };

  const handleDeleteAttachment = (urlToDelete) => {
    formik.setFieldValue('attachments', formik.values.attachments.filter((url) => url !== urlToDelete));
  };

  const handleCcTempChange = (event) => {
    formik.handleChange(event);
    if (ccError) {
      setCcError('');
    }
  };

  const uploadToS3 = async (selectedFiles) => {
    try {
      setUploading(true);
      setUploadProgress({});

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      const maxSize = 10 * 1024 * 1024;
      const validFiles = Array.from(selectedFiles).filter(
        (file) => allowedTypes.includes(file.type) && file.size <= maxSize
      );

      if (validFiles.length === 0) {
        throw new Error('No valid files selected. Only PDF, JPEG, and PNG files under 10MB are allowed.');
      }

      const propertyId = soaData?.property_details?.property_id?._id || soaData?.property_details?.property_id;
      if (!propertyId) {
        throw new Error('Property ID is missing.');
      }

      const response = await uploadDocuments(propertyId, validFiles, (event, fileName) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress((prev) => ({ ...prev, [fileName]: progress }));
        }
      });

      if (!response.success || !response.data?.length) {
        throw new Error(response.message || 'Failed to upload files.');
      }

      const newUrls = response.data.map((item) => getEncodedUrl(item.fileUrl));
      formik.setFieldValue('attachments', [...formik.values.attachments, ...newUrls]);
      setUploading(false);
      setUploadError(null);
      setFiles([]);
      setIsUploadOpen(false);

      Swal.fire({
        title: 'Files Uploaded',
        text: `${newUrls.length} file(s) uploaded successfully.`,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      setUploadError(err.message || 'Failed to upload files.');
      setUploading(false);
    }
  };

  const handleFileChange = (event) => {
    const selectedFiles = event.target.files;
    if (selectedFiles.length > 0) {
      setFiles(Array.from(selectedFiles));
      setUploadError(null);
      uploadToS3(selectedFiles);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      setFiles(Array.from(droppedFiles));
      setUploadError(null);
      uploadToS3(droppedFiles);
    }
  };

  const handleCancelUpload = () => {
    if (uploading) {
      Swal.fire({
        title: 'Cancel Upload?',
        text: 'Are you sure you want to cancel the upload?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel upload',
        cancelButtonText: 'No, continue upload',
      }).then((result) => {
        if (result.isConfirmed) {
          setUploading(false);
          setFiles([]);
          setIsUploadOpen(false);
        }
      });
    } else {
      setFiles([]);
      setIsUploadOpen(false);
    }
  };

  const handlePreviewAttachment = async (url) => {
    if (isUploadOpen) {
      setIsUploadOpen(false);
    }
    try {
      const { key, fileType } = extractS3KeyAndType(url);
      if (!key) throw new Error('Invalid file key.');
      const response = await getSignedUrl(key, 'get', fileType);
      if (response.success && response.data.signedUrl) {
        setPreviewUrl(response.data.signedUrl);
      } else {
        throw new Error('Failed to generate preview URL.');
      }
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err.message || 'Failed to generate preview URL.',
        icon: 'error',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  const handleDownloadAttachment = async (url) => {
    try {
      const { key, fileType } = extractS3KeyAndType(url);
      if (!key) throw new Error('Invalid file key.');
      const response = await getSignedUrl(key, 'get', fileType);
      if (response.success && response.data.signedUrl) {
        const link = document.createElement('a');
        link.href = response.data.signedUrl;
        link.download = key.split('/').pop() || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('Failed to generate download URL.');
      }
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err.message || 'Failed to download file.',
        icon: 'error',
        timer: 2000,
        showConfirmButton: false,
      });
    }
  };

  const getAttachmentFileName = (url) => {
    try {
      return decodeURIComponent(url.split('/').pop()) || 'Document';
    } catch {
      return 'Document';
    }
  };

  const isSOAPdf = (url) => {
    return url.includes('Rental_Account_Summary_') && url.toLowerCase().endsWith('.pdf');
  };

  const displayedAttachments = formik.values.attachments.filter((url) => !isSOAPdf(url));

  return (
    <Box className="soa-send-tenant-email" sx={{ mt: 3 }}>
      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          fontFamily: '"Times New Roman", Times, serif',
        }}
      >
        <AccordionSummary
          expandIcon={<FeatherIcon icon={expanded ? 'chevron-up' : 'chevron-down'} size={16} />}
          aria-controls="send-tenant-email-content"
          id="send-tenant-email-header"
          sx={{
            bgcolor: 'grey.100',
            '& .MuiAccordionSummary-content': { alignItems: 'center' },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              fontSize: '0.8rem',
              color: '#1976d2',
              textTransform: 'uppercase',
              fontFamily: '"Times New Roman", Times, serif',
            }}
          >
            <FeatherIcon icon="mail" size={16} style={{ marginRight: '8px' }} />
            Send Email to Tenant
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          {!soaData ? (
            <Alert severity="error" sx={{ mx: 2, py: 1, fontFamily: '"Times New Roman", Times, serif' }}>
              <FeatherIcon icon="alert-triangle" size={16} sx={{ mr: 1 }} />
              No email data found.
            </Alert>
          ) : (
            <>
              {soaData.email_details?.emailed_at && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: 'text.secondary',
                    fontFamily: '"Times New Roman", Times, serif',
                  }}
                >
                  <FeatherIcon icon="clock" size={16} sx={{ mr: 1 }} />
                  Email last sent on: {new Date(soaData.email_details.emailed_at).toLocaleString()}
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Recipient Name"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                      size="small"
                      sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Recipient Email"
                      name="email"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                      size="small"
                      sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        fullWidth
                        label="CC Email"
                        name="ccTemp"
                        value={formik.values.ccTemp}
                        onChange={handleCcTempChange}
                        onBlur={formik.handleBlur}
                        error={(formik.touched.ccTemp && Boolean(formik.errors.ccTemp)) || Boolean(ccError)}
                        helperText={(formik.touched.ccTemp && formik.errors.ccTemp) || ccError}
                        size="small"
                        sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                      />
                      <IconButton
                        onClick={handleAddCc}
                        disabled={!formik.values.ccTemp || formik.errors.ccTemp || Boolean(ccError)}
                      >
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formik.values.cc.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          onDelete={email === managerEmail ? undefined : () => handleDeleteCc(email)}
                          deleteIcon={email === managerEmail ? null : <DeleteIcon />}
                          sx={{
                            fontFamily: '"Times New Roman", Times, serif',
                            bgcolor: email === managerEmail ? 'grey.200' : undefined,
                            opacity: email === managerEmail ? 0.7 : 1,
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        fullWidth
                        label="BCC Email"
                        name="bccTemp"
                        value={formik.values.bccTemp}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.bccTemp && Boolean(formik.errors.bccTemp)}
                        helperText={formik.touched.bccTemp && formik.errors.bccTemp}
                        size="small"
                        sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                      />
                      <IconButton
                        onClick={handleAddBcc}
                        disabled={!formik.values.bccTemp || formik.errors.bccTemp}
                      >
                        <AddCircleOutlineIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formik.values.bcc.map((email, index) => (
                        <Chip
                          key={index}
                          label={email}
                          onDelete={() => handleDeleteBcc(email)}
                          deleteIcon={<DeleteIcon />}
                          sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formik.values.subject}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.subject && Boolean(formik.errors.subject)}
                      helperText={formik.touched.subject && formik.errors.subject}
                      size="small"
                      sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      name="notes"
                      multiline
                      rows={4}
                      value={formik.values.notes}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.notes && Boolean(formik.errors.notes)}
                      helperText={formik.touched.notes && formik.errors.notes}
                      size="small"
                      sx={{ fontFamily: '"Times New Roman", Times, serif' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 1,
                        fontWeight: 'bold',
                        color: '#1976d2',
                        fontFamily: '"Times New Roman", Times, serif',
                      }}
                    >
                      <FeatherIcon icon="paperclip" size={16} sx={{ mr: 1 }} />
                      Additional Attachments (Optional)
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<FeatherIcon icon="upload" size={16} />}
                      onClick={() => setIsUploadOpen(true)}
                      disabled={uploading || isSubmitting}
                      sx={{ mb: 2, fontFamily: '"Times New Roman", Times, serif' }}
                    >
                      Upload Files
                    </Button>
                    {displayedAttachments.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                          Current Attachments:
                        </Typography>
                        {displayedAttachments.map((url, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                            }}
                          >
                            <FeatherIcon icon="file" size={16} />
                            <Typography
                              variant="body2"
                              sx={{ flexGrow: 1, fontFamily: '"Times New Roman", Times, serif' }}
                            >
                              {getAttachmentFileName(url)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => handlePreviewAttachment(url)}
                                title="Preview"
                                sx={{ color: 'primary.main' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadAttachment(url)}
                                title="Download"
                                sx={{ color: 'success.main' }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAttachment(url)}
                                title="Remove"
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSendEmail}
                        disabled={isSubmitting || uploading || generatingSOAPdf}
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <FeatherIcon icon="send" size={16} />
                          )
                        }
                        sx={{ fontFamily: '"Times New Roman", Times, serif', zIndex: 1000 }}
                      >
                        {isSubmitting ? 'Sending Email...' : 'Send Email'}
                      </Button>

                      {allApprovalsDone && (
                        <Button
                          variant="outlined"
                          color="success"
                          startIcon={<FeatherIcon icon="download" size={16} />}
                          onClick={handleDownloadSOA}
                          disabled={downloadingSOAPdf}
                          sx={{
                            fontFamily: '"Times New Roman", Times, serif',
                            textTransform: 'none',
                            fontWeight: 'bold',
                          }}
                        >
                          {downloadingSOAPdf ? 'Generating PDF...' : 'Download SOA PDF'}
                          {downloadingSOAPdf && <CircularProgress size={14} sx={{ ml: 1 }} />}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={isUploadOpen}
        onClose={uploading ? undefined : handleCancelUpload}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        BackdropProps={{ 'aria-hidden': false }}
        PaperProps={{ sx: { borderRadius: 2, p: 1 }, ...(isUploadOpen ? {} : { inert: '' }) }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Upload Additional Files</Typography>
          <IconButton onClick={handleCancelUpload} disabled={uploading}>
            <CancelIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <FormControl>
              <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 1, ml: -1 }}>
                File Upload
              </InputLabel>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'grey.400',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'grey.600' },
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                role="button"
                aria-label="Upload file"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={uploading}
                  accept="application/pdf,image/jpeg,image/png"
                  multiple
                />
                <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500', mb: 2 }} />
                <Typography variant="body1" sx={{ mt: 2 }}>
                  {files.length > 0
                    ? `${files.length} file(s) selected`
                    : 'Drag & drop files here or click to browse'}
                </Typography>
                {files.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {files.map((file, index) => (
                      <Typography key={index} variant="body2" color="text.secondary">
                        {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </FormControl>
          </Box>
          {uploading && (
            <Box sx={{ width: '100%', mb: 2 }}>
              {files.map((file) => (
                <Box key={file.name} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">{file.name}</Typography>
                    <Typography variant="body2">{uploadProgress[file.name] || 0}%</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress[file.name] || 0}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelUpload} disabled={uploading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {previewUrl && (
        <Dialog
          open={Boolean(previewUrl)}
          onClose={() => setPreviewUrl(null)}
          maxWidth="md"
          fullWidth
          disableEnforceFocus
          BackdropProps={{ 'aria-hidden': false }}
          PaperProps={{ ...(previewUrl ? {} : { inert: '' }) }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Attachment Preview</Typography>
            <IconButton onClick={() => setPreviewUrl(null)}>
              <CancelIcon fontSize="medium" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {previewUrl.endsWith('.pdf') ? (
              <iframe
                src={previewUrl}
                title="PDF Preview"
                style={{ width: '100%', height: '500px', border: 'none' }}
              />
            ) : (
              <img
                src={previewUrl}
                alt="Attachment Preview"
                style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button startIcon={<DownloadIcon />} onClick={() => handleDownloadAttachment(previewUrl)}>
              Download
            </Button>
            <Button onClick={() => setPreviewUrl(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export { SendSOAToTenant };